import React from "react";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";

export default function Service() {
    return (
        <>
            <Navbar isDownloadVisible={false} />
            <section className="policy-section">
                <h1 className="policy-title">Terms of Service</h1>
                <ol>
                    <li>
                        <h2 className="policy-section-title">
                            Acceptance of Terms
                        </h2>
                        <p>
                            <strong>Last updated: August 15, 2025</strong>
                        </p>
                        <p>
                            By installing or using the Oakmate Chrome extension
                            (the "Service"), you agree to be bound by these
                            Terms of Service. If you do not agree to these
                            terms, do not install or use Oakmate. Oakmate
                            reserves the right to update or modify these Terms
                            at any time; continued use of the Service after any
                            changes constitutes acceptance of the new Terms.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Service Description
                        </h2>
                        <p>
                            Oakmate is a Chrome extension that delivers instant
                            post-game chess analysis on Chess.com and Lichess
                            using public APIs and a browser-based Stockfish
                            v17.1 engine. Free features include move
                            classification, best-move comparison, Elo/accuracy
                            estimates, custom puzzles from mistakes, real-time
                            reviews with alternative lines, a retry mode,
                            time-accuracy charts, opening stats, and adjustable
                            engine depth. Upcoming Pro features will add an AI
                            Coach with human-like explanations, end-of-analysis
                            puzzles, cloud analysis, and long-term progression
                            insights. All analysis runs locally, except Pro
                            cloud features which use secure servers, and Oakmate
                            never alters site data or scrapes without
                            permission.
                        </p>
                        <p>
                            All game data is processed locally in your browser
                            except Pro cloud analysis which uses secure servers.
                            Oakmate never alters original site data or uses
                            unauthorized scraping.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Account Registration
                        </h2>
                        <p>
                            To use Oakmate, you must create an account with a
                            valid email address. We collect only your email for
                            account identification; no other personal
                            information is required. You are responsible for
                            maintaining the confidentiality of your account
                            credentials. We use Google Analytics (GA4) to track
                            anonymous usage metrics.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Data Collection and API Usage
                        </h2>
                        <p>
                            Oakmate retrieves chess games exclusively through
                            official public APIs:
                        </p>
                        <ul>
                            <li>
                                <strong>Chess.com API:</strong>{" "}
                                <a
                                    href="https://api.chess.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    api.chess.com
                                </a>
                            </li>
                            <li>
                                <strong>Lichess API:</strong>{" "}
                                <a
                                    href="https://lichess.org/api"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    lichess.org/api
                                </a>
                            </li>
                        </ul>
                        <p>We collect and process:</p>
                        <ul>
                            <li>
                                <strong>Email Address:</strong> For account
                                creation and essential communication
                            </li>
                            <li>
                                <strong>Game Data:</strong> Moves, results,
                                timestamps accessed via public APIs
                            </li>
                            <li>
                                <strong>Cloud Analysis Data (Pro):</strong> Game
                                data temporarily processed on secure AWS servers
                                for Pro users (automatically deleted after
                                analysis)
                            </li>
                            <li>
                                <strong>Analytics:</strong> Anonymous usage
                                metrics via Google Analytics
                            </li>
                        </ul>
                        <p>
                            We never store or access your Chess.com/Lichess
                            passwords. Personal data is never sold or shared.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Subscription Plans and Payment
                        </h2>
                        <p>Oakmate operates on a freemium model:</p>
                        <ul>
                            <li>
                                <strong>Free Tier:</strong> Includes all current
                                analysis features at no cost
                            </li>
                            <li>
                                <strong>Pro Tier:</strong> $5/month or $30/year
                                (50% savings) for upcoming advanced features
                            </li>
                        </ul>
                        <p>
                            Payments are processed through Stripe via secure
                            checkout at{" "}
                            <a
                                href="https://oakmate.app"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                oakmate.app
                            </a>
                            . Subscriptions automatically renew until canceled.
                            You may manage subscriptions through the /account
                            page on oakmate.app. Pro features are currently in
                            development and will automatically activate for
                            subscribers upon release.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Technical Implementation
                        </h2>
                        <p>Oakmate utilizes the following technologies:</p>
                        <ul>
                            <li>
                                <strong>Stockfish 17.1:</strong> WebAssembly
                                implementation (GPLv3 licensed) for
                                browser-based analysis
                            </li>
                            <li>
                                <strong>Browser Storage:</strong> IndexedDB for
                                local game caching and puzzle generation
                            </li>
                            <li>
                                <strong>Frontend:</strong> React-based popup
                                interface
                            </li>
                            <li>
                                <strong>APIs:</strong> Official Chess.com and
                                Lichess REST APIs for compliant data access
                            </li>
                        </ul>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Prohibited Conduct
                        </h2>
                        <p>When using Oakmate, you must not:</p>
                        <ul>
                            <li>
                                Modify Oakmate for real-time game assistance
                                (cheating) during active games
                            </li>
                            <li>
                                Reverse engineer or attempt to access Oakmate's
                                source code
                            </li>
                            <li>
                                Share Pro account access across multiple users
                            </li>
                            <li>
                                Bypass API usage limits on Chess.com or Lichess
                            </li>
                            <li>
                                Use automated tools to mass-analyze games beyond
                                personal use
                            </li>
                            <li>
                                Misrepresent analysis results as official
                                platform evaluations
                            </li>
                        </ul>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Intellectual Property
                        </h2>
                        <p>
                            Oakmate retains all rights to its software,
                            branding, and analysis methodologies. Stockfish is
                            used under GPLv3 (
                            <a
                                href="https://stockfishchess.org"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                stockfishchess.org
                            </a>
                            ). Game data remains property of respective
                            platforms. You may not resell Oakmate's analysis
                            outputs.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Disclaimer of Warranties
                        </h2>
                        <p>
                            OAKMATE IS PROVIDED "AS IS" WITHOUT WARRANTIES.
                            Analysis accuracy varies by engine depth and
                            hardware capabilities. Cloud analysis speeds depend
                            on server availability. Coming soon features have no
                            guaranteed release timeline.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Limitation of Liability
                        </h2>
                        <p>
                            OAKMATE'S MAXIMUM LIABILITY IS LIMITED TO PRO
                            SUBSCRIPTION FEES PAID IN THE PREVIOUS 3 MONTHS. WE
                            ARE NOT LIABLE FOR:
                        </p>
                        <ul>
                            <li>
                                Platform API changes affecting functionality
                            </li>
                            <li>
                                Analysis discrepancies from engine limitations
                            </li>
                            <li>Browser compatibility issues</li>
                            <li>Delays in Pro feature development</li>
                        </ul>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Subscription Management
                        </h2>
                        <p>
                            Pro subscriptions auto-renew monthly/annually.
                            Cancel anytime via oakmate.app/account. No refunds
                            for partial periods. Upon cancellation, Pro features
                            deactivate immediately. Failed payments result in
                            automatic downgrade to Free tier.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Service Modifications
                        </h2>
                        <p>
                            Oakmate may update features or engine versions
                            without notice. Browser extension updates deploy
                            automatically via Chrome Web Store. We reserve the
                            right to:
                        </p>
                        <ul>
                            <li>Reprioritize Pro feature development</li>
                            <li>Adjust analysis methodologies</li>
                            <li>Modify API usage patterns</li>
                            <li>Discontinue features with 30 days notice</li>
                        </ul>
                    </li>
                    <li>
                        <h2 className="policy-section-title">Governing Law</h2>
                        <p>
                            These Terms are governed by French law. Disputes
                            will be resolved in courts of Paris, France.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Contact Information
                        </h2>
                        <p>
                            For support or inquiries: contact@oakmate.app
                            <br />
                            Full terms available at{" "}
                            <a
                                href="https://oakmate.app/policy/service"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                oakmate.app/policy/service
                            </a>
                        </p>
                    </li>
                </ol>
            </section>
            <footer className="FooterSection-Container">
                <Footer />
            </footer>
        </>
    );
}
